/**
 * Utility to capture, persist, and automatically inject UTM parameters into Umami analytics calls.
 */
/**
 * Removes the stored referer from localStorage.
 */
export function clearStoredReferer(): void {
  try {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('referer');
      localStorage.removeItem('referrer');
    }
  } catch (e) {
    console.error('Failed to clear referer from localStorage:', e);
  }
}

/**
 * Gets the stored referer from localStorage.
 */
export function getStoredReferer(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('referer') || localStorage.getItem('referrer');
  } catch {
    return null;
  }
}

/**
 * Sets the stored referer in localStorage.
 */
export function setStoredReferer(referer: string): void {
  try {
    if (typeof window !== 'undefined' && referer) {
      localStorage.setItem('referer', referer);
    }
  } catch (e) {
    console.error('Failed to set referer in localStorage:', e);
  }
}

export function initializeUtmTracking(): void {
  if (typeof window === 'undefined') return;

  // 1. Capture UTM parameters and referer, storing them in localStorage
  try {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      if (key.toLowerCase().startsWith('utm_')) {
        localStorage.setItem(key.toLowerCase(), value);
      }
    });

    const pathname = window.location.pathname;
    // Determine if this is the start/beginning of the process:
    // Landing page ('/' or '') or step 1 ('/step/1').
    // Any page other than mid-process steps (/step/2..5) or admin is considered the beginning.
    const isBeginningOfProcess =
      pathname === '/' ||
      pathname === '' ||
      pathname === '/step/1' ||
      pathname.startsWith('/step/1') ||
      (!pathname.startsWith('/step/') && !pathname.startsWith('/admin'));

    const queryReferrer =
      urlParams.get('ref') ||
      urlParams.get('referrer') ||
      urlParams.get('utm_referrer');

    const rawReferrer = document.referrer;
    let isExternalReferrer = false;
    if (rawReferrer) {
      try {
        const refUrl = new URL(rawReferrer);
        if (refUrl.hostname !== window.location.hostname) {
          isExternalReferrer = true;
        }
      } catch {
        isExternalReferrer = true;
      }
    }

    // At the very beginning of the process:
    // Remove the referer from localStorage first.
    // If a new referer is set (via query parameter or external document.referrer), store it.
    // Otherwise, leave it empty.
    if (isBeginningOfProcess) {
      clearStoredReferer();
      if (queryReferrer) {
        setStoredReferer(queryReferrer);
      } else if (isExternalReferrer) {
        setStoredReferer(rawReferrer);
      }
    } else if (queryReferrer) {
      // If user landed on another step directly with query parameter, store it
      setStoredReferer(queryReferrer);
    } else if (isExternalReferrer && !getStoredReferer()) {
      // If arriving from external referrer and not already stored
      setStoredReferer(rawReferrer);
    }
  } catch (e) {
    console.error('Failed to store UTM parameters or referer:', e);
  }

  // 2. Global fetch interceptor to append UTM parameters and referer to all Umami analytics calls
  try {
    const originalFetch = window.fetch;
    window.fetch = function (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const urlString =
        typeof input === 'string'
          ? input
          : input instanceof Request
            ? input.url
            : input.toString();

      if (urlString.includes('/api/send') && init && init.body) {
        try {
          const bodyStr =
            typeof init.body === 'string' ? init.body : init.body.toString();
          const body = JSON.parse(bodyStr);

          if (body && body.payload) {
            // Gather all stored UTM params from localStorage
            const utmParams: Record<string, string> = {};
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && key.startsWith('utm_')) {
                const val = localStorage.getItem(key);
                if (val) {
                  utmParams[key] = val;
                }
              }
            }

            const storedReferer = getStoredReferer();
            if (storedReferer) {
              body.payload.referrer = storedReferer;
            }

            if (Object.keys(utmParams).length > 0 || storedReferer) {
              // Append UTM parameters to payload URL
              if (body.payload.url && Object.keys(utmParams).length > 0) {
                try {
                  const urlObj = new URL(
                    body.payload.url,
                    window.location.origin,
                  );
                  Object.entries(utmParams).forEach(([key, val]) => {
                    if (!urlObj.searchParams.has(key)) {
                      urlObj.searchParams.set(key, val);
                    }
                  });
                  body.payload.url =
                    urlObj.pathname + urlObj.search + urlObj.hash;
                } catch (e) {
                  const query = Object.entries(utmParams)
                    .map(
                      ([k, v]) =>
                        `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
                    )
                    .join('&');
                  body.payload.url +=
                    (body.payload.url.includes('?') ? '&' : '?') + query;
                }
              }

              // For event types, also inject UTM parameters and referer as custom properties
              if (body.type === 'event') {
                body.payload.data = {
                  ...(body.payload.data || {}),
                  ...utmParams,
                  ...(storedReferer ? { referer: storedReferer } : {}),
                };
              }

              init.body = JSON.stringify(body);
            }
          }
        } catch (err) {
          console.error('Error in Umami fetch interceptor:', err);
        }
      }

      return originalFetch.apply(this, [input, init]);
    };
  } catch (e) {
    console.error('Failed to set up Umami fetch interceptor:', e);
  }
}
